import { toEnvironment, toSite } from './mappers';
/**
 * MOM applies its own list limit when a request carries no `limit` and caps a client-supplied one
 * well below that, so pages are asked for by number only. It never reports the page size it used —
 * hence the end of the list is taken from its own `nextPage`/`total` metadata rather than from a
 * page-size constant here, which would silently skip rows the day MOM changed its default.
 */
const MAX_ASSIGNMENT_PAGES = 100;
const assignmentsPath = (entityId, studyId, environmentId) => `/v1/entities/${entityId}/studies/${studyId}/environments/${environmentId}/environment-site-consent-assignments`;
const normalizeMomListResponse = (payload) => {
    const isEnvelope = 'data' in payload;
    const unwrapped = isEnvelope ? payload.data : payload;
    const total = isEnvelope ? payload.total : undefined;
    const nextPage = isEnvelope ? payload.nextPage : undefined;
    return {
        assignments: Array.isArray(unwrapped?.assignments) ? unwrapped.assignments : [],
        total: typeof total === 'number' ? total : undefined,
        nextPage: nextPage === null || typeof nextPage === 'string' ? nextPage : undefined,
    };
};
/** Every assignment row in one environment, following MOM's paging rather than taking page one. */
const listAssignmentRows = async (momClient, entityId, studyId, environmentId) => {
    const path = assignmentsPath(entityId, studyId, environmentId);
    const rows = [];
    for (let page = 1; page <= MAX_ASSIGNMENT_PAGES; page++) {
        const { data } = await momClient.get(path, {
            params: { page },
        });
        const { assignments, total, nextPage } = normalizeMomListResponse(data);
        rows.push(...assignments);
        if (assignments.length === 0) {
            break;
        }
        // `nextPage` is MOM's own answer to "is there more"; responses carrying only `total` are
        // measured against what has arrived. With neither, this is the whole list — probing for
        // another page would spend a request on every Site tab load disproving a page MOM never
        // claimed, and page one is exactly what this function returned before it paged at all.
        const hasMorePages = nextPage !== undefined ? nextPage !== null : total !== undefined && rows.length < total;
        if (!hasMorePages) {
            break;
        }
    }
    return rows;
};
export async function getSites(momClient, entityId, studyId) {
    const { data: { data: momSites }, } = await momClient.get(`/v1/entities/${entityId}/studies/${studyId}/sites`);
    return momSites.map(toSite);
}
export async function getEnvironments(momClient, entityId, studyId) {
    const { data: { data: momEnvironments }, } = await momClient.get(`/v1/entities/${entityId}/studies/${studyId}/environments`);
    return momEnvironments.map(toEnvironment);
}
/**
 * Loads site ↔ consent-template assignments for a single environment.
 *
 * MOM stores one row per (environmentSiteId, consentTemplateKey). Rows whose key is not
 * present in the current consent files are dropped so the UI never offers a stale lineage.
 */
export async function getAssignments(momClient, entityId, studyId, environmentId, consentFiles) {
    const rows = await listAssignmentRows(momClient, entityId, studyId, environmentId);
    const knownKeys = new Set(consentFiles.map((file) => file.templateKey));
    return rows
        .filter((row) => knownKeys.has(row.consentTemplateKey))
        .map((row) => ({
        id: row.key,
        environmentSiteId: row.environmentSiteId,
        consentTemplateKey: row.consentTemplateKey,
    }));
}
